import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Download, Eye, Paperclip, Pin } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Badge } from '@/components/ui/badge'
import { AnnouncementLinkCard } from '@/components/announcements/announcement-link-card'
import { useAnnouncement } from '@/hooks/use-announcements'
import { downloadDocument, isViewableMimeType, viewDocument } from '@/lib/documents'
import { formatDate } from '@/lib/utils'
import type { AnnouncementCategory } from '@riverpark/types'

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return ''
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const CATEGORY_BADGE: Record<
  AnnouncementCategory,
  { variant: 'overdue' | 'pending' | 'credit' | 'committee' | 'default'; label: string }
> = {
  urgent: { variant: 'overdue', label: 'Urgent' },
  maintenance: { variant: 'pending', label: 'Maintenance' },
  financial: { variant: 'credit', label: 'Financial' },
  governance: { variant: 'committee', label: 'Governance' },
  general: { variant: 'default', label: 'General' },
}

export function AnnouncementDetailPage({ basePath }: { basePath: string }) {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useAnnouncement(id)
  const announcement = data?.data

  const backLink = (
    <Link
      to={basePath}
      className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Back to notices
    </Link>
  )

  if (isLoading) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      </PageShell>
    )
  }

  if (!announcement) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-destructive">Announcement not found.</p>
        <div className="mt-4">{backLink}</div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHeader
        label="Estate"
        title={announcement.title}
        description={`${announcement.authorName} · ${formatDate(new Date(announcement.createdAt * 1000))}`}
        action={backLink}
      />

      <div className="max-w-2xl space-y-4">
        <div className="flex items-center gap-2">
          {announcement.pinned && <Pin size={14} className="text-primary" />}
          <Badge variant={CATEGORY_BADGE[announcement.category].variant}>
            {CATEGORY_BADGE[announcement.category].label}
          </Badge>
          {announcement.status === 'draft' && <Badge variant="draft">Draft</Badge>}
        </div>
        <div
          className="font-sans text-sm leading-relaxed text-ink [&_a]:text-primary [&_a]:underline [&_img]:my-3 [&_img]:max-w-full [&_img]:rounded [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: announcement.body }}
        />

        {announcement.links.length > 0 && (
          <div className="space-y-2 pt-2">
            <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Related
            </p>
            <div className="space-y-2">
              {announcement.links.map((link) => (
                <AnnouncementLinkCard key={link.id} link={link} basePath={basePath} />
              ))}
            </div>
          </div>
        )}

        {announcement.attachments.length > 0 && (
          <div className="space-y-2 pt-2">
            <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Attachments
            </p>
            <ul className="space-y-1.5">
              {announcement.attachments.map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-center justify-between gap-3 rounded border border-border-2 bg-card px-3 py-2"
                >
                  <span className="flex min-w-0 items-center gap-2 font-sans text-sm text-ink">
                    <Paperclip size={14} className="shrink-0 text-muted-foreground" />
                    <span className="truncate">{doc.title}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatFileSize(doc.sizeBytes)}
                    </span>
                  </span>
                  <div className="flex shrink-0 items-center gap-1">
                    {isViewableMimeType(doc.mimeType) && (
                      <button
                        type="button"
                        onClick={() => viewDocument(doc)}
                        className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
                        aria-label={`View ${doc.title}`}
                      >
                        <Eye size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => downloadDocument(doc)}
                      className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
                      aria-label={`Download ${doc.title}`}
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PageShell>
  )
}
